import { DataSourceItem } from '@lendi-ui/auto-complete';
import { Brand } from '@lendi/core-constants';
import { Store, TeamMember, UserRole } from '../../../interfaces';
import { TeamMemberType } from 'api';

export const transformDataSource = (data: TeamMember[]): DataSourceItem[] => {
  return Array.isArray(data)
    ? data
        .map((item) => ({
          label: item.firstName + ' ' + item.lastName,
          value: JSON.stringify(item),
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    : [];
};

export const getStaticData = (value: string, data: DataSourceItem[]) => {
  return data.filter((item) => item.label.toLowerCase().indexOf(value.toLowerCase()) > -1);
};

/**
 * getTeamMembersPrimaryStore()
 *
 * Retrieves team members primary store based on `linkedStores` & existing array ordering implementation from server.
 *
 * A team members primary store is guaranteed as the first value by array ordering set when we consume team member events
 * and store them into the team members table.
 *
 * @see: https://bitbucket.org/lendi-dev/appointment-booking-service/src/a214baadd1663cce7196dc67b8bd747981f4cbe1/src/teams-worker/consumer/handlers/teamMember/index.ts#lines-17
 *
 ** Note: This method will return undefined when the primary store is not returned in the team members store data,
 **       even if other stores are available, if this just returned any store if the primary was not found, that would be
 **       extremely misleading for implementation purposes.
 *
 * @param teamMember
 */
export const getTeamMembersPrimaryStore = (teamMember: TeamMember): Store | undefined => {
  const isAussieTeamMember = teamMember?.brand === Brand.Aussie;
  const teamMemberHasLinkedStores = teamMember?.linkedStores && teamMember?.linkedStores.length > 0;
  const teamMemberHasStoreData = teamMember?.stores && teamMember?.stores.length > 0;
  const canFindPrimaryStore = teamMemberHasLinkedStores && teamMemberHasStoreData;

  if (!isAussieTeamMember || !canFindPrimaryStore) return undefined;

  /**
   * Note: teamMember?.linkedStores check shouldn't be needed here, typescripts isn't inferring correctly from above, think it's
   * because we run a really old version of TS
   */
  const teamMemberPrimaryStoreId = teamMember?.linkedStores && teamMember.linkedStores[0];

  /**
   * Note: teamMember?.stores check shouldn't be needed here, typescript isn't inferring correctly from above, think it's
   * because we run a really old version of TS.
   */
  return teamMember?.stores && teamMember?.stores.find((store) => store.id === teamMemberPrimaryStoreId);
};

export const canRoleBookMyselfF2F = (teamMember: TeamMember | undefined) => {
  switch (teamMember?.userRole) {
    case 'LENDI_HOME_LOAN_CONSULTANT':
    case 'RETAIL_BROKER':
    case 'FRANCHISEE_BROKER':
    case 'MOBILE_BROKER':
      return true;
    default:
      return false;
  }
};

export const isAssociateBroker = (role: UserRole) => {
  switch (role) {
    case 'LENDI_ASSOCIATE':
    case 'CLIENT_SOLUTIONS_ASSESSMENT_ASSOCIATE':
    case 'CLIENT_SOLUTIONS_SETTLEMENT_ASSOCIATE':
    case 'AUSSIE_ASSOCIATE':
      return true;
    default:
      return false;
  }
};

export const bookableTeamMemberRoleToType = (role: UserRole) => {
  switch (role) {
    case 'BUYERS_AGENT_ASSOCIATE':
    case 'MOBILE_BUYERS_AGENT':
    case 'TEAM_BUYERS_AGENT':
      return TeamMemberType.BuyersAgent;
    default:
      return TeamMemberType.Broker;
  }
};
